import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Search, Plus, ArrowLeft, Globe, Lock, Loader2, LayoutTemplate,
  Copy, Trash2, FolderInput, Info,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  loadFolders, TEMPLATE_TYPES, getTemplatesByType, createFolder, updateFolder, deleteFolder,
  duplicateFolder, addTemplateEntry, updateTemplateEntry, removeTemplateEntry, duplicateTemplateEntry,
  moveTemplateEntry, getPublicTemplates, copyFolderToMyLibrary,
} from '@/lib/templateFolders';
import { ProductionToolsDropdown } from '@/components/production/ProductionToolsDropdown';
import { TemplateFolderCard } from '@/components/templates/TemplateFolderCard';
import { TemplateFolderDialog } from '@/components/templates/TemplateFolderDialog';

const API_URL = window.__BACKEND_URL;

/** Short, human-readable summary of what a template entry actually captured — a Level 1/2 "preview" without a type-specific renderer for every tool. */
function describeEntry(et) {
  if (et.scheduling) { const s = et.scheduling; return `${(s.platforms || []).join(', ') || 'No platforms'} · ${(s.postingDays || []).join('/') || 'any day'} · ${(s.postingTimes || []).join(', ') || 'no times set'}`; }
  if (et.optimization) { const o = et.optimization; return `"${o.title || 'Untitled'}" · ${[...(o.primaryKeywords || []), ...(o.secondaryKeywords || [])].slice(0, 4).join(', ') || 'no keywords'}`; }
  if (et.golive) { const g = et.golive; return `${(g.alertMappings || []).filter((a) => a.sound_name).length} alert sound(s) · ${g.intro ? 'intro' : 'no intro'} · ${g.outro ? 'outro' : 'no outro'}`; }
  if (et.design) { const d = et.design; return `${d.kind === 'thumbnail' ? 'Thumbnail' : d.kind} design · ${d.aspectRatio || ''} · ${(d.elements || d.pages?.[0]?.elements || []).length} element(s)`; }
  if ((et.segments?.length || 0) > 0 || (et.outputs?.length || 0) > 0) return `${et.segments?.length || 0} segment(s) · ${et.outputs?.length || 0} expected output(s)`;
  return 'Blueprint only — no settings captured yet. Apply it from its tool, then save over it.';
}

export default function TemplatesWorkspace() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  const returnTo = searchParams.get('returnTo');
  const creatorId = searchParams.get('creatorId') || localStorage.getItem('activeCreatorId');

  const [tab, setTab] = useState('mine');
  const [folders, setFolders] = useState([]);
  const [publicFolders, setPublicFolders] = useState([]);
  const [loadingPublic, setLoadingPublic] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [visibilityFilter, setVisibilityFilter] = useState('all');

  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [detailFolder, setDetailFolder] = useState(null);
  const [newEntryName, setNewEntryName] = useState({});
  const [renaming, setRenaming] = useState(null);

  const refresh = () => { if (creatorId) setFolders(loadFolders(creatorId)); };
  useEffect(refresh, [creatorId]);
  useEffect(() => {
    if (tab !== 'public' || !creatorId) return;
    setLoadingPublic(true);
    getPublicTemplates(API_URL, creatorId).then(setPublicFolders).finally(() => setLoadingPublic(false));
  }, [tab, creatorId]);

  const list = tab === 'mine' ? folders : publicFolders;
  const visible = useMemo(() => {
    let out = list;
    if (tab === 'mine' && visibilityFilter !== 'all') out = out.filter((f) => f.visibility === visibilityFilter);
    if (typeFilter !== 'all') out = out.filter((f) => getTemplatesByType(f, typeFilter).length > 0);
    const q = search.trim().toLowerCase();
    if (q) out = out.filter((f) => (f.name || '').toLowerCase().includes(q) || (f.description || '').toLowerCase().includes(q));
    return out;
  }, [list, tab, visibilityFilter, typeFilter, search]);

  // Keep the detail dialog's folder object in sync with the library after any mutation.
  useEffect(() => { if (detailFolder) setDetailFolder(folders.find((f) => f.id === detailFolder.id) || null); }, [folders]);

  const openCreate = () => { setEditingFolder(null); setFolderDialogOpen(true); };
  const openEdit = (folder) => { setEditingFolder(folder); setFolderDialogOpen(true); };
  const handleSaveFolder = (draft) => {
    if (editingFolder) { setFolders(updateFolder(creatorId, editingFolder.id, draft)); toast.success(`Saved "${draft.name}"`); }
    else { const { folders: next, folder } = createFolder(creatorId, draft); setFolders(next); toast.success(`Created Template Folder "${folder.name}"`); }
    setFolderDialogOpen(false);
  };
  const handleDuplicateFolder = (folder) => { setFolders(duplicateFolder(creatorId, folder.id)); toast.success(`Duplicated "${folder.name}"`); };
  const confirmDelete = () => { if (!deleteTarget) return; setFolders(deleteFolder(creatorId, deleteTarget.id)); toast.success(`Deleted "${deleteTarget.name}". Any content already produced from its templates is untouched.`); setDeleteTarget(null); if (detailFolder?.id === deleteTarget.id) setDetailFolder(null); };
  const handleCopyToMyLibrary = (folder) => { const { folders: next, folder: copy } = copyFolderToMyLibrary(creatorId, folder); setFolders(next); toast.success(`Copied "${folder.name}" into your library as "${copy.name}"`); setTab('mine'); };

  const handleAddEntry = (folder, type) => {
    const name = (newEntryName[`${folder.id}-${type}`] || '').trim();
    if (!name) { toast.error('Give the new template a name first'); return; }
    const { folders: next } = addTemplateEntry(creatorId, folder.id, type, { name, segments: [], outputs: [] });
    setFolders(next);
    setNewEntryName((m) => ({ ...m, [`${folder.id}-${type}`]: '' }));
    toast.success(`Added "${name}" — open its Production Tool and apply it to fill in the real settings.`);
  };
  const handleRenameEntry = (folder, entry, name) => { if (!name.trim()) return; setFolders(updateTemplateEntry(creatorId, folder.id, entry.id, { name: name.trim() })); setRenaming(null); };
  const handleDuplicateEntry = (folder, entry) => { setFolders(duplicateTemplateEntry(creatorId, folder.id, entry.id)); toast.success(`Duplicated "${entry.name}"`); };
  const handleDeleteEntry = (folder, entry) => { setFolders(removeTemplateEntry(creatorId, folder.id, entry.id)); toast.success(`Removed "${entry.name}"`); };
  const handleMoveEntry = (folder, entry, toFolderId) => { if (!toFolderId || toFolderId === folder.id) return; setFolders(moveTemplateEntry(creatorId, folder.id, toFolderId, entry.id)); toast.success(`Moved "${entry.name}" to "${folders.find((f) => f.id === toFolderId)?.name}"`); };

  const returnLink = returnTo ? (projectId ? `${returnTo}${returnTo.includes('?') ? '&' : '?'}projectId=${projectId}` : returnTo) : null;

  return (
    <div className="container mx-auto p-6 max-w-7xl" data-testid="templates-workspace">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="text-xs text-[#8A93A6] mb-1">
            <Link to="/app/create" className="hover:text-white">Create</Link><span className="mx-1.5 opacity-50">/</span>
            <span className="text-[#C0C7D4]">Templates</span>
          </p>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2" data-testid="templates-workspace-title"><LayoutTemplate className="h-6 w-6 text-[#3A7BFF]" />Templates</h1>
          <p className="text-sm text-[#8A93A6] mt-1">Organize reusable templates into Template Folders, link them to a project, and reuse the same production workflow every episode.</p>
        </div>
        <div className="flex items-center gap-2">
          <ProductionToolsDropdown currentTool="templates" creatorId={creatorId} projectId={projectId} returnTo={returnLink} />
          {returnLink && (
            <Button variant="outline" className="border-white/10" onClick={() => navigate(returnLink)} data-testid="templates-return-to-tool-btn"><ArrowLeft className="h-4 w-4 mr-2" />Return to tool</Button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white/5">
            <TabsTrigger value="mine" data-testid="templates-tab-mine">My Templates</TabsTrigger>
            <TabsTrigger value="public" data-testid="templates-tab-public">Public Templates</TabsTrigger>
          </TabsList>
        </Tabs>
        {tab === 'mine' && (
          <Button className="bg-[#3A7BFF] hover:bg-[#3A7BFF]/80" onClick={openCreate} data-testid="templates-create-folder-btn"><Plus className="h-4 w-4 mr-2" />Create Template Folder</Button>
        )}
      </div>

      {tab === 'public' && (
        <div className="flex items-start gap-2 text-xs text-[#8A93A6] bg-white/[0.03] border border-white/5 rounded-lg px-3 py-2.5 mb-4" data-testid="templates-public-level3-note">
          <Info className="h-3.5 w-3.5 text-[#3A7BFF] shrink-0 mt-0.5" />
          <span>Showing Public folders from your other creator profiles in this browser. Platform-wide public template discovery across every creator and device isn't connected yet (Level 3) — nothing here is another account's private data.</span>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 text-[#8A93A6] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search Template Folders" className="pl-9 bg-white/5 border-white/10 text-white" data-testid="templates-search-input" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[190px] bg-white/5 border-white/10 text-white" data-testid="templates-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent className="bg-[#1C1F26] border-white/10">
            <SelectItem value="all">All types</SelectItem>
            {TEMPLATE_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {tab === 'mine' && (
          <Select value={visibilityFilter} onValueChange={setVisibilityFilter}>
            <SelectTrigger className="w-[150px] bg-white/5 border-white/10 text-white" data-testid="templates-visibility-filter"><SelectValue placeholder="All visibility" /></SelectTrigger>
            <SelectContent className="bg-[#1C1F26] border-white/10">
              <SelectItem value="all">All visibility</SelectItem>
              <SelectItem value="private">Private</SelectItem>
              <SelectItem value="public">Public</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {tab === 'public' && loadingPublic ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 text-[#8A93A6] animate-spin" /></div>
      ) : visible.length === 0 ? (
        <div className="text-center py-20" data-testid="templates-workspace-empty">
          <LayoutTemplate className="h-10 w-10 text-[#3A3F4B] mx-auto mb-3" />
          <p className="text-sm text-[#8A93A6]">{list.length === 0 ? (tab === 'mine' ? 'No Template Folders yet.' : 'None of your other creator profiles in this browser have a Public folder yet.') : 'No folders match your filters.'}</p>
          {tab === 'mine' && list.length === 0 && <Button className="mt-4 bg-[#3A7BFF] hover:bg-[#3A7BFF]/80" onClick={openCreate} data-testid="templates-empty-create-btn"><Plus className="h-4 w-4 mr-2" />Create your first Template Folder</Button>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="templates-folder-grid">
          {visible.map((f) => (
            <TemplateFolderCard key={f.id} folder={f} isPublic={tab === 'public'} onOpen={setDetailFolder} onEdit={openEdit} onDuplicate={handleDuplicateFolder} onDelete={setDeleteTarget} onCopyToMyLibrary={handleCopyToMyLibrary} />
          ))}
        </div>
      )}

      <TemplateFolderDialog open={folderDialogOpen} onOpenChange={setFolderDialogOpen} folder={editingFolder} onSave={handleSaveFolder} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#14171F] border-white/10" data-testid="templates-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#8A93A6]">This removes the folder and unlinks it from any project. Content already produced from its templates (clips, designs, posts, etc.) is never deleted. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 bg-transparent text-white hover:bg-white/5" data-testid="templates-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="templates-delete-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!detailFolder} onOpenChange={(v) => !v && setDetailFolder(null)}>
        <DialogContent className="bg-[#14171F] border-white/10 sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="templates-folder-detail-dialog">
          {detailFolder && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <LayoutTemplate className="h-5 w-5 text-[#3A7BFF]" />{detailFolder.name}
                  <Badge variant="outline" className={`text-[9px] gap-1 ${detailFolder.visibility === 'public' ? 'border-green-500/30 text-green-300' : 'border-white/10 text-[#8A93A6]'}`}>
                    {detailFolder.visibility === 'public' ? <Globe className="h-2.5 w-2.5" /> : <Lock className="h-2.5 w-2.5" />}{detailFolder.visibility === 'public' ? 'Public' : 'Private'}
                  </Badge>
                </DialogTitle>
              </DialogHeader>
              {detailFolder.description && <p className="text-sm text-[#8A93A6] -mt-3">{detailFolder.description}</p>}
              {((detailFolder.segments?.length || 0) > 0 || (detailFolder.outputs?.length || 0) > 0) && (
                <div className="flex items-center gap-2 text-xs text-[#8A93A6] bg-white/[0.03] border border-white/5 rounded-lg px-3 py-2">
                  <span className="text-white">{detailFolder.content_type}</span>{detailFolder.series_name && <span>· {detailFolder.series_name}</span>}
                  <span>· {detailFolder.segments.length} segment(s) · {detailFolder.outputs.length} output(s){detailFolder.use_brand_kit_defaults ? ' · Brand Kit defaults on' : ''}</span>
                  <Button size="sm" variant="ghost" className="ml-auto h-6 text-[10px] text-[#3A7BFF]" onClick={() => openEdit(detailFolder)} data-testid="templates-detail-edit-blueprint-btn">Edit</Button>
                </div>
              )}

              <div className="space-y-5 mt-2">
                {TEMPLATE_TYPES.map((t) => {
                  const entries = getTemplatesByType(detailFolder, t.key);
                  const addKey = `${detailFolder.id}-${t.key}`;
                  return (
                    <div key={t.key}>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-[#8A93A6] text-xs uppercase tracking-wider">{t.label}s <span className="text-[#555E6E] normal-case">· used in {t.tool}</span></Label>
                      </div>
                      <div className="space-y-1.5">
                        {entries.length === 0 && <p className="text-xs text-[#555E6E]">None yet.</p>}
                        {entries.map((et) => (
                          <div key={et.id} className="bg-white/[0.03] border border-white/5 rounded-lg px-3 py-2" data-testid={`templates-detail-entry-${et.id}`}>
                            <div className="flex items-center gap-2">
                              {renaming === et.id ? (
                                <Input autoFocus defaultValue={et.name} onBlur={(e) => handleRenameEntry(detailFolder, et, e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleRenameEntry(detailFolder, et, e.target.value)} className="h-7 text-xs bg-black/40 border-white/10 flex-1" data-testid={`templates-detail-rename-input-${et.id}`} />
                              ) : (
                                <button onClick={() => setRenaming(et.id)} className="text-sm text-white text-left flex-1 truncate hover:underline" data-testid={`templates-detail-rename-trigger-${et.id}`}>{et.name}</button>
                              )}
                              <button onClick={() => handleDuplicateEntry(detailFolder, et)} className="text-[#8A93A6] hover:text-white" title="Duplicate" data-testid={`templates-detail-duplicate-${et.id}`}><Copy className="h-3.5 w-3.5" /></button>
                              <Select onValueChange={(v) => handleMoveEntry(detailFolder, et, v)}>
                                <SelectTrigger className="h-7 w-9 px-1.5 justify-center border-none bg-transparent text-[#8A93A6] hover:text-white" title="Move to another folder" data-testid={`templates-detail-move-${et.id}`}><FolderInput className="h-3.5 w-3.5" /></SelectTrigger>
                                <SelectContent className="bg-[#1C1F26] border-white/10">
                                  {folders.filter((f) => f.id !== detailFolder.id).map((f) => <SelectItem key={f.id} value={f.id}>Move to "{f.name}"</SelectItem>)}
                                </SelectContent>
                              </Select>
                              <button onClick={() => handleDeleteEntry(detailFolder, et)} className="text-[#8A93A6] hover:text-red-400" title="Delete" data-testid={`templates-detail-delete-${et.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                            </div>
                            <p className="text-[11px] text-[#555E6E] mt-1">{describeEntry(et)}</p>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-1.5 mt-2">
                        <Input value={newEntryName[addKey] || ''} onChange={(e) => setNewEntryName((m) => ({ ...m, [addKey]: e.target.value }))} placeholder={`New ${t.label.toLowerCase()} name`} className="h-7 text-xs bg-black/40 border-white/10" data-testid={`templates-detail-add-input-${t.key}`} />
                        <Button size="sm" variant="outline" className="h-7 border-white/10 text-xs shrink-0" onClick={() => handleAddEntry(detailFolder, t.key)} data-testid={`templates-detail-add-btn-${t.key}`}><Plus className="h-3 w-3 mr-1" />Add</Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
